import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { apiService } from '../services/api';
import SEO from '../components/SEO';

const DemoScheduleCallPage: React.FC = () => {
  const [contactName, setContactName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const canSubmit = useMemo(() => {
    if (loading) return false;
    if (contactName.trim().length < 2) return false;
    if (businessName.trim().length < 2) return false;
    if (zipCode.replace(/\D/g, '').length < 5) return false;
    if (!email.trim()) return false;
    if (phone.replace(/\D/g, '').length < 10) return false;
    return true;
  }, [loading, contactName, businessName, zipCode, email, phone]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!canSubmit) {
      setError('Please fill in all fields (name, business, ZIP, phone, and email).');
      return;
    }
    setLoading(true);
    try {
      const res = await apiService.post('/leads/demo-schedule-call', {
        contactName: contactName.trim(),
        businessName: businessName.trim(),
        zipCode: zipCode.trim(),
        email: email.trim(),
        phone: phone.trim(),
      });
      if (!res.success) {
        throw new Error((res as any).error || 'Could not submit request');
      }
      setDone(true);
    } catch (err: any) {
      setError(err?.message || 'Could not submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <SEO
        title="Schedule a Call | LawnHub Demo"
        description="Request a call or virtual meet after trying the LawnHub interactive demo."
      />
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 } }}>
        {done ? (
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5 }}>
              You’re on the list
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Thanks — we’ll reach out soon to schedule a call or virtual meet. In the meantime, you
              can create your free account whenever you’re ready.
            </Typography>
            <Button
              component={RouterLink}
              to="/get-started"
              variant="contained"
              size="large"
              sx={{ fontWeight: 700, textTransform: 'none' }}
            >
              Create your free account
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Schedule a call / virtual meet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Leave your details and we’ll follow up. All fields are required.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Name"
                required
                fullWidth
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                autoComplete="name"
              />
              <TextField
                label="Business name"
                required
                fullWidth
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                autoComplete="organization"
              />
              <TextField
                label="ZIP code"
                required
                fullWidth
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                autoComplete="postal-code"
              />
              <TextField
                label="Phone number"
                required
                fullWidth
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
              />
              <TextField
                label="Email"
                required
                type="email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!canSubmit}
                sx={{ mt: 1, fontWeight: 800, textTransform: 'none', py: 1.25 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Request a call'}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                We use your information to contact you about LawnHub. See our{' '}
                <Link component={RouterLink} to="/privacy-policy" target="_blank" rel="noopener">
                  Privacy Policy
                </Link>
                .
              </Typography>
              <Button
                component={RouterLink}
                to="/demo/exit"
                color="inherit"
                sx={{ textTransform: 'none' }}
              >
                Back
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default DemoScheduleCallPage;
